import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Auth, authState } from '@angular/fire/auth';
import { AumicoCoreProvider } from '@modeso/aumico-lib-core-fe';
import { UntilDestroy } from '@ngneat/until-destroy';
import { from, Observable, of } from 'rxjs';
import { catchError, filter, finalize, map, switchMap, take } from 'rxjs/operators';
import { MfaFilter } from '../interfaces/mfa-filter.interface';
import { RoutesUtil } from '../util/RoutesUtil';

export interface MfaCheckResponse {
    required: boolean;
    status: 'NOT_REQUIRED' | 'SETUP_REQUIRED' | 'VERIFY_REQUIRED';
}

export interface MfaOrgStatusResponse {
    isEnabled: boolean;
}

export interface MfaSetupInitiateResponse {
    qrCodeDataUrl: string;
    secret: string;
}

export interface MfaSetupVerifyResponse {
    backupCodes: string[];
}

export interface MfaUserStatus {
    userId: string;
    email: string;
    firstName: string;
    lastName: string;
    isMfaSetupComplete: boolean;
    enabledAt?: string;
}

@UntilDestroy()
@Injectable({
    providedIn: 'root',
})
export class MfaService {
    constructor(
        private readonly auth: Auth,
        private readonly http: HttpClient,
        private readonly coreProvider: AumicoCoreProvider,
        private readonly routesUtil: RoutesUtil,
    ) { }

    /**
     * Check whether MFA is required for the current user.
        * This call only retrieves MFA requirement status.
        * Token refresh is handled explicitly by calling forceTokenRefresh$().
     */
    checkMfa$(): Observable<MfaCheckResponse> {
        this.coreProvider.dispatchBlockingRequest('MfaService_checkMfa$');
        const url = this.routesUtil.getMfaCheckUrl();
        return this.http
            .get<MfaCheckResponse>(url)
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('MfaService_checkMfa$')));
    }

    /**
     * Force-refresh the Firebase ID token so new custom claims (2FA_SETUP / 2FA_QUERY / real roles)
     * are picked up by the Angular interceptor on subsequent requests.
     * Waits for Firebase auth to resolve a non-null user before refreshing, to avoid
     * the race condition where currentUser is null during auth state transitions.
     */
    forceTokenRefresh$(): Observable<string> {
        return authState(this.auth).pipe(
            filter((user) => user !== null),
            take(1),
            switchMap((user) => from(user!.getIdToken(true))),
        );
    }

    setupInitiate$(): Observable<MfaSetupInitiateResponse> {
        this.coreProvider.dispatchBlockingRequest('MfaService_setupInitiate$');
        const url = this.routesUtil.getMfaSetupInitiateUrl();
        return this.http
            .post<MfaSetupInitiateResponse>(url, {})
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('MfaService_setupInitiate$')));
    }

    setupVerify$(code: string): Observable<MfaSetupVerifyResponse> {
        this.coreProvider.dispatchBlockingRequest('MfaService_setupVerify$');
        const url = this.routesUtil.getMfaSetupVerifyUrl();
        return this.http
            .post<MfaSetupVerifyResponse>(url, { code })
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('MfaService_setupVerify$')));
    }

    verify$(code: string): Observable<void> {
        this.coreProvider.dispatchBlockingRequest('MfaService_verify$');
        const url = this.routesUtil.getMfaVerifyUrl();
        return this.http
            .post<void>(url, { code })
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('MfaService_verify$')));
    }

    getOrgMfaStatus$(): Observable<MfaOrgStatusResponse> {
        this.coreProvider.dispatchBlockingRequest('MfaService_getOrgMfaStatus$');
        const url = this.routesUtil.getMfaOrgStatusUrl();
        return this.http
            .get<MfaOrgStatusResponse>(url)
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('MfaService_getOrgMfaStatus$')));
    }

    enableMfa$(): Observable<void> {
        this.coreProvider.dispatchBlockingRequest('MfaService_enableMfa$');
        const url = this.routesUtil.getMfaOrgEnableUrl();
        return this.http
            .post<void>(url, {})
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('MfaService_enableMfa$')));
    }

    disableMfa$(): Observable<void> {
        this.coreProvider.dispatchBlockingRequest('MfaService_disableMfa$');
        const url = this.routesUtil.getMfaOrgDisableUrl();
        return this.http
            .post<void>(url, {})
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('MfaService_disableMfa$')));
    }

    getUsersMfaStatus$(filter?: MfaFilter): Observable<MfaUserStatus[]> {
        this.coreProvider.dispatchBlockingRequest('MfaService_getUsersMfaStatus$');
        const url = this.routesUtil.getMfaOrgUsersUrl();
        const params = this.routesUtil.buildMfaHttpParams(filter);
        return this.http
            .get<MfaUserStatus[]>(url, { params })
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('MfaService_getUsersMfaStatus$')));
    }

    resetUserMfa$(userId: string): Observable<void> {
        this.coreProvider.dispatchBlockingRequest('MfaService_resetUserMfa$');
        const url = this.routesUtil.getMfaResetUserUrl(userId);
        return this.http
            .post<void>(url, {})
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('MfaService_resetUserMfa$')));
    }

    /**
     * Fetch the current user's organization name.
     * Returns null if the request fails (e.g. insufficient token claims at MFA stage).
     */
    getCurrentOrganizationName$(): Observable<string | null> {
        const url = this.routesUtil.getCurrentOrganizationUrl();
        return this.http.get<{ name: string }>(url).pipe(
            map((org) => org.name ?? null),
            catchError(() => of(null)),
        );
    }
}
